import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { AuthorizedMediaUrlResolver } from '../../../media/AuthorizedMediaUrlResolver';
import { Logger } from '../../../support/Logger';
import { DeviceInfo } from '../../../support/DeviceInfo';
import { ToastPresenter } from '../../../ToastBinder';
import { blobToBase64 } from '../../../utils/blobToBase64';

const attachmentsDirPath = 'attachments';
const COULD_NOT_OPEN = 'Sorry, that file could not be opened';

export interface IAttachmentOpenRequest {
    // the stored url, which also keys the device's cached copy
    url: string;
    fileName: string;
    // the authorised route, when the server offered one
    href?: string;
    onDownloading?: (downloading: boolean) => void;
}

// '8' FILE_NOT_SUPPORTED
// https://github.com/capacitor-community/file-opener?tab=readme-ov-file#api
const isFileNotSupported = (err: unknown): boolean =>
    typeof err === 'object' && err !== null && 'code' in err && err.code === '8';

// Opens a stored file the way a caregiver expects on each platform: downloaded and handed to the
// device's viewer on native, a new tab on web, so every caller reads through the audited route
// rather than the stored bucket url.
//
// Call it straight from a click handler, without awaiting anything first: on web the tab must be
// opened before the url resolves, or the browser treats it as an unsolicited popup and blocks it.
// It never rejects, because its callers do not await it: every failure is reported to the
// caregiver and to Sentry here.
export class AttachmentOpener {

    static inject = () => [AuthorizedMediaUrlResolver, Logger, ToastPresenter, DeviceInfo];
    constructor(
        private resolver: AuthorizedMediaUrlResolver,
        private log: Logger,
        private toastPresenter: ToastPresenter,
        private deviceInfo: Pick<DeviceInfo, 'platform'>
    ) {}

    open = async (request: IAttachmentOpenRequest): Promise<void> => {
        if (this.deviceInfo.platform !== 'web') {
            await this.openOnDevice(request);
        } else {
            await this.openInTab(request);
        }
    };

    // Resolved when the file is opened rather than when it is rendered. The credential in an
    // authorised url lasts minutes, and a note can sit open for far longer than that, so a url
    // baked in at render is one the server would refuse by the time anyone clicked it.
    private resolveNow = async (request: IAttachmentOpenRequest): Promise<string | undefined> => {
        const { url, fileName, href } = request;
        try {
            return await this.resolver.resolve({ url, href });
        } catch (e) {
            this.log.error(e, { context: 'could not authorise an attachment read', fileName });
            this.toastPresenter.toast({ duration: 3000, message: COULD_NOT_OPEN });
            return undefined;
        }
    };

    private openOnDevice = async (request: IAttachmentOpenRequest): Promise<void> => {
        const resolvedUrl = await this.resolveNow(request);
        if (!resolvedUrl) {
            // resolveNow has already told the caregiver and Sentry. Opening the file is impossible
            // without an authorised url, and falling back to the stored one would be an unaudited
            // read of the public bucket.
            return;
        }

        try {
            const filePath = await this.cachedFileUri({ ...request, resolvedUrl });
            await FileOpener.open({ openWithDefault: true, filePath });
        } catch (err) {
            // logged so we can see whether any specific errors need handling
            this.log.error(err, { context: 'could not open an attachment on the device', fileName: request.fileName });

            if (isFileNotSupported(err)) {
                const link = document.createElement('a');
                link.href = resolvedUrl;
                link.target = '_blank';
                link.click();
            } else {
                this.toastPresenter.toast({ duration: 3000, message: COULD_NOT_OPEN });
            }
        }
    };

    // The device's cached copy, downloaded first if this is the first time it is opened.
    private cachedFileUri = async (params: IAttachmentOpenRequest & { resolvedUrl: string }): Promise<string> => {
        const { url, fileName, resolvedUrl, onDownloading } = params;

        // Keyed on the stable bucket url, NOT the resolved one: that carries a session token, so it
        // would change every session (defeating the cache) and write a live credential into a path
        // on disk.
        const base64S3Path = btoa(encodeURIComponent(url));
        const filePath = `${attachmentsDirPath}/${base64S3Path}/${fileName.replace(/\//g, '-')}`;

        try {
            const { uri } = await Filesystem.stat({ path: filePath, directory: Directory.Cache });
            return uri;
        } catch {
            // Not cached yet, which is what stat reports as an error: download it below.
        }

        // shows an indicator in case the download is slow
        onDownloading?.(true);
        try {
            // android needs us to create the directories first
            if (this.deviceInfo.platform === 'android') {
                await Filesystem.mkdir({
                    recursive: true,
                    directory: Directory.Cache,
                    path: `${attachmentsDirPath}/${base64S3Path}`
                });
            }

            const path = await this.saveToCache({ url: resolvedUrl, filePath });
            if (!path) {
                throw new Error(`Filesystem.downloadFile failed? ${filePath} was not downloaded successfully`);
            }
            return path;
        } finally {
            onDownloading?.(false);
        }
    };

    // A file uploaded on this device and not saved yet resolves to a local blob url, which
    // Filesystem.downloadFile cannot fetch, so its bytes are written out directly instead.
    private saveToCache = async (params: { url: string; filePath: string }): Promise<string | undefined> => {
        const { url, filePath } = params;

        if (url.startsWith('blob:')) {
            const blob = await (await fetch(url)).blob();
            const { uri } = await Filesystem.writeFile({
                path: filePath,
                data: await blobToBase64(blob),
                directory: Directory.Cache,
                recursive: true
            });
            return uri;
        }

        const { path } = await Filesystem.downloadFile({
            url,
            path: filePath,
            directory: Directory.Cache
        });
        return path;
    };

    private openInTab = async (request: IAttachmentOpenRequest): Promise<void> => {
        // Opened before awaiting so the browser still attributes it to the click; one opened after
        // the await is treated as an unsolicited popup and blocked.
        const tab = window.open('', '_blank');

        // Severed by hand rather than by window.open's 'noopener' feature, which would return null
        // and leave nothing to navigate once the url resolves. Without this the file, whose content
        // type we do not control, keeps a live handle on the app window and could redirect it out
        // from under the caregiver.
        if (tab) {
            tab.opener = null;
        }

        const resolvedUrl = await this.resolveNow(request);
        if (!resolvedUrl) {
            // Closed rather than left sitting: a blank tab the caregiver has to notice and dismiss
            // reads as the app hanging. resolveNow has already reported the failure.
            tab?.close();
            return;
        }

        if (tab) {
            tab.location.href = resolvedUrl;
        }
    };
}
